import React, { useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import EmptyAddCardCst from "@components/EmptyAddCardCst";
import ItemCardCst from "@components/ItemCardCst";
import { Stakeholder } from "@components/StakeholdersCst";
import StakeholdersPickerCst from "@components/StakeholdersPickerCst";

interface StakeholdersFieldCstProps {
  label: string;
  addLabel: string;
  values: Stakeholder[];
  onChange: (next: Stakeholder[]) => void;
  disabled?: boolean;
}

const StakeholdersFieldCst: React.FC<StakeholdersFieldCstProps> = ({
  label,
  addLabel,
  values,
  onChange,
  disabled = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("stakeholders-picker");

  const [pickerOpen, setPickerOpen] = useState(false);

  const excludeUserIds = values
    .map((s) => s.userId)
    .filter((id): id is number => typeof id === "number");

  const handleConfirm = (stakeholder: Stakeholder) => {
    onChange([...values, stakeholder]);
  };

  const handleRemove = (idx: number) => {
    const next = values.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <Box>
      <Typography
        sx={{
          fontSize: "13px",
          fontWeight: 500,
          color: theme.palette.text.primary,
          mb: 1,
        }}
      >
        {label}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {values.map((stakeholder, idx) => (
          <ItemCardCst
            key={`stkhldr_${stakeholder.userId}_${idx}`}
            title={stakeholder.name || `#${stakeholder.userId}`}
            segments={[
              stakeholder.type,
              stakeholder.visStakeholder
                ? t("visible-to-learner")
                : t("not-visible-to-learner"),
            ]}
            onDelete={disabled ? undefined : () => handleRemove(idx)}
          />
        ))}

        {!disabled && (
          <EmptyAddCardCst
            addLabel={addLabel}
            onAdd={() => setPickerOpen(true)}
            minHeight={values.length > 0 ? "64px" : "120px"}
          />
        )}
      </Box>

      <StakeholdersPickerCst
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title={t("title") || "Aggiungi Stakeholder"}
        excludeUserIds={excludeUserIds}
        onConfirm={handleConfirm}
      />
    </Box>
  );
};

export default StakeholdersFieldCst;
